import React, { useEffect, useState, createContext, useContext } from 'react';
// Supported languages with their codes and names
export const SUPPORTED_LANGUAGES = {
  en: {
    name: 'English',
    flag: '🇺🇸'
  },
  es: {
    name: 'Spanish',
    flag: '🇪🇸'
  },
  fr: {
    name: 'French',
    flag: '🇫🇷'
  },
  de: {
    name: 'German',
    flag: '🇩🇪'
  },
  it: {
    name: 'Italian',
    flag: '🇮🇹'
  },
  pt: {
    name: 'Portuguese',
    flag: '🇵🇹'
  },
  ru: {
    name: 'Russian',
    flag: '🇷🇺'
  },
  zh: {
    name: 'Chinese',
    flag: '🇨🇳'
  },
  ja: {
    name: 'Japanese',
    flag: '🇯🇵'
  },
  ko: {
    name: 'Korean',
    flag: '🇰🇷'
  },
  ar: {
    name: 'Arabic',
    flag: '🇸🇦'
  },
  hi: {
    name: 'Hindi',
    flag: '🇮🇳'
  },
  nl: {
    name: 'Dutch',
    flag: '🇳🇱'
  },
  sv: {
    name: 'Swedish',
    flag: '🇸🇪'
  },
  fi: {
    name: 'Finnish',
    flag: '🇫🇮'
  },
  da: {
    name: 'Danish',
    flag: '🇩🇰'
  },
  no: {
    name: 'Norwegian',
    flag: '🇳🇴'
  },
  pl: {
    name: 'Polish',
    flag: '🇵🇱'
  },
  tr: {
    name: 'Turkish',
    flag: '🇹🇷'
  },
  th: {
    name: 'Thai',
    flag: '🇹🇭'
  }
};
type LanguageCode = keyof typeof SUPPORTED_LANGUAGES;
interface TranslationContextType {
  isTranslationEnabled: boolean;
  toggleTranslation: () => void;
  userLanguage: LanguageCode;
  setUserLanguage: (code: LanguageCode) => void;
  angelLanguage: LanguageCode;
  setAngelLanguage: (code: LanguageCode) => void;
  translateMessage: (message: string, sourceLanguage: LanguageCode, targetLanguage: LanguageCode) => Promise<string>;
  detectLanguage: (message: string) => Promise<LanguageCode>;
  translationInProgress: boolean;
  translationError: string | null;
}
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);
interface TranslationProviderProps {
  children: ReactNode;
}
export const TranslationProvider: React.FC<TranslationProviderProps> = ({
  children
}) => {
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(true);
  const [userLanguage, setUserLanguage] = useState<LanguageCode>('en');
  const [angelLanguage, setAngelLanguage] = useState<LanguageCode>('en');
  const [translationInProgress, setTranslationInProgress] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  // Toggle translation on/off
  const toggleTranslation = () => {
    setIsTranslationEnabled(!isTranslationEnabled);
  };
  // Mock function to detect language (in a real app, this would call a language detection API)
  const detectLanguage = async (message: string): Promise<LanguageCode> => {
    setTranslationInProgress(true);
    try {
      // In a real implementation, this would call an API like DeepL or Google's language detection
      // For now, we'll simulate a delay and return a mock result
      await new Promise(resolve => setTimeout(resolve, 300));
      // This is a placeholder - in reality, you'd analyze the text
      const detectedLanguage = 'en' as LanguageCode;
      setTranslationInProgress(false);
      return detectedLanguage;
    } catch (error) {
      setTranslationError('Failed to detect language');
      setTranslationInProgress(false);
      return 'en'; // Default fallback
    }
  };
  // Mock function to translate messages (in a real app, this would call DeepL or GPT-4o)
  const translateMessage = async (message: string, sourceLanguage: LanguageCode, targetLanguage: LanguageCode): Promise<string> => {
    if (sourceLanguage === targetLanguage) return message;
    if (!isTranslationEnabled) return message;
    setTranslationInProgress(true);
    setTranslationError(null);
    try {
      // In a real implementation, this would call DeepL API first, then fallback to GPT-4o if needed
      // For now, we'll simulate a delay and return a mock result
      await new Promise(resolve => setTimeout(resolve, 500));
      // Mock translation - in reality, you'd call the translation API
      let translatedText = message;
      // Just for demonstration, append a note if languages differ
      if (sourceLanguage !== targetLanguage) {
        translatedText = `${message} [Translated from ${SUPPORTED_LANGUAGES[sourceLanguage].name} to ${SUPPORTED_LANGUAGES[targetLanguage].name}]`;
      }
      setTranslationInProgress(false);
      return translatedText;
    } catch (error) {
      setTranslationError('Translation failed. Try again.');
      setTranslationInProgress(false);
      return message; // Return original on error
    }
  };
  // Expose the context values
  const value = {
    isTranslationEnabled,
    toggleTranslation,
    userLanguage,
    setUserLanguage,
    angelLanguage,
    setAngelLanguage,
    translateMessage,
    detectLanguage,
    translationInProgress,
    translationError
  };
  return <TranslationContext.Provider value={value} data-id="element-21589">
      {children}
    </TranslationContext.Provider>;
};
// Custom hook to use the translation context
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};